import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { HouseTypeIcon } from './HouseTypeIcon';
import type { House } from '../../types/house';
import type { UsageProfile } from '../../types/scan';
import { HOUSE_TYPES, HOUSE_TYPE_LABELS, LABEL_COLORS } from '../../utils/house';
import { num } from '../../utils/format';
interface StepWoningProps {
  house: House;
  usage: UsageProfile;
  onChange: (patch: Partial<UsageProfile>) => void;
  onNext: () => void;
}
export function StepWoning({
  house,
  usage,
  onChange,
  onNext
}: StepWoningProps) {
  const isApartment = usage.houseType === 'appartement';
  return <div data-id="element-652" data-mp="oxkiml9knful">
      <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-653" data-mp="ozeyvm9wejsc">We hebben je woning gevonden.</h1>
      <p className="mt-3 text-lg text-ink-soft" data-id="element-654" data-mp="k0u2xz1oim9p">Klopt het woningtype? Pas het aan als het niet klopt.</p>

      <fieldset className="mt-8" data-id="element-655" data-mp="ezeedx0alxmk">
        <legend className="text-[15px] font-medium text-ink" data-id="element-656" data-mp="aykudepz12yy">Type woning</legend>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5" data-id="element-657" data-mp="b0ykd95snozn">
          {HOUSE_TYPES.map(t => {
          const active = usage.houseType === t;
          return <label key={t} className={`flex cursor-pointer flex-col items-center gap-2 rounded-2xl border bg-white px-2 py-4 text-center transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${active ? 'border-ink' : 'border-line hover:border-ink/40'}`} data-id="element-658" data-mp="1g18mckfwt8op">
                <input type="radio" name="houseType" value={t} checked={active} onChange={() => onChange({
              houseType: t,
              ...(t === 'appartement' ? {
                hasSolar: false
              } : {})
            })} className="sr-only" data-id="element-659" data-mp="1yxqj5g9m7yrt" />
                <HouseTypeIcon type={t} active={active} data-id="element-660" data-mp="9ik4a0nrr6df" />
                <span className="whitespace-nowrap text-[14px] font-medium text-ink" data-id="element-661" data-mp="dlp764brha2l">{HOUSE_TYPE_LABELS[t]}</span>
              </label>;
        })}
        </div>
      </fieldset>

      <div className="mt-10 grid gap-8 sm:grid-cols-[1.1fr_1fr]" data-id="element-662" data-mp="2b8hlba9zqrqr">
        <div className="rounded-3xl bg-forest p-6 text-canvas" data-id="element-663" data-mp="2bb41fyay6fkl">
          <div className="text-[14px] text-canvas/60" data-id="element-664" data-mp="26gzuvg5lg9k1">Ruimte op je dak</div>
          {isApartment ? <>
              <div className="mt-2 text-3xl font-semibold tracking-tight" data-id="element-665" data-mp="21iy4ea5u97yj">Gedeeld dak</div>
              <p className="mt-3 text-[15px] leading-relaxed text-canvas/70" data-id="element-666" data-mp="1xixo0jz6imou">
                Bij een appartement beslist de VvE over het dak. Een batterij werkt ook zonder eigen panelen, met een
                dynamisch contract.
              </p>
            </> : <>
              <div className="mt-2 flex items-baseline gap-2" data-id="element-667" data-mp="1ilwxgjsq4xry">
                <span className="text-5xl font-semibold tracking-tight text-volt tabular-nums" data-id="element-668" data-mp="3hxpuuhjfc557">{house.panelPotential}</span>
                <span className="text-lg text-canvas/80" data-id="element-669" data-mp="3czo9tr2ftvr3">zonnepanelen</span>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-canvas/70" data-id="element-670" data-mp="3lem1lv8hcm1p">
                Op basis van {num(house.roofArea)} m² dakoppervlak, gemeten op de luchtfoto.
              </p>
            </>}
        </div>

        <dl className="grid grid-cols-2 content-start gap-x-6 border-t border-line" data-id="element-671" data-mp="3u9s9czrb9e6v">
          <div className="border-b border-line py-4" data-id="element-672" data-mp="30miewkiq0eq">
            <dt className="text-[13px] text-ink-muted" data-id="element-673" data-mp="32h17o9quyxa8">Bouwjaar</dt>
            <dd className="mt-0.5 text-xl font-medium text-ink" data-id="element-674" data-mp="32ivxo2gzlfl6">{house.buildYear}</dd>
          </div>
          <div className="border-b border-line py-4" data-id="element-675" data-mp="36jxw4h32xnm3">
            <dt className="text-[13px] text-ink-muted" data-id="element-676" data-mp="3bgfzpdmobfwu">Woonoppervlak</dt>
            <dd className="mt-0.5 text-xl font-medium text-ink" data-id="element-677" data-mp="3ggtys66arfiq">{num(house.livingArea)} m²</dd>
          </div>
          <div className="border-b border-line py-4" data-id="element-678" data-mp="qrs0lidxl4s7">
            <dt className="text-[13px] text-ink-muted" data-id="element-679" data-mp="qtcquvt7q1k9">Footprint</dt>
            <dd className="mt-0.5 text-xl font-medium text-ink" data-id="element-680" data-mp="voidxxycpm9x">{num(house.footprintArea)} m²</dd>
          </div>
          <div className="border-b border-line py-4" data-id="element-681" data-mp="24n9zmna3gu3w">
            <dt className="text-[13px] text-ink-muted" data-id="element-682" data-mp="1zop2wxu1wsk7">Energielabel (indicatie)</dt>
            <dd className="mt-1" data-id="element-683" data-mp="2dng9lz6l3byb">
              <span className="inline-flex h-7 min-w-[44px] items-center justify-center rounded-md px-2 text-[14px] font-semibold text-white" style={{
              backgroundColor: LABEL_COLORS[house.energyLabel]
            }} data-id="element-684" data-mp="2dohn0ab2oaj7">
                {house.energyLabel}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-10 flex justify-end" data-id="element-685" data-mp="2wdxzlw017tfz">
        <button type="button" onClick={onNext} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-ink px-6 py-4 text-[16px] font-medium text-canvas transition-colors duration-150 hover:bg-forest" data-id="element-686" data-mp="2wdo7o2vffc7n">
          Klopt, naar verbruik
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-687" data-mp="31eu52e017nni" />
        </button>
      </div>
    </div>;
}